import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import {
  Database,
  List,
  Layers,
  Building2,
  Layout,
  ScrollText,
  Settings,
  Activity,
  Warehouse,
  BarChart3,
  Users,
} from "lucide-react";
import { HotelAccounts } from "./internal/HotelAccounts";
import { SaasAccounts } from "./internal/SaasAccounts";
import { InternalLogs } from "./internal/InternalLogs";
import { PMSTable } from "./internal/PMSTable";
import { PMSScopes } from "./internal/PMSScopes";
import { InternalSettings } from "./internal/InternalSettings";
import { QueueMonitoring } from "./internal/QueueMonitoring";
import { WarehouseMonitoring } from "./internal/WarehouseMonitoring";
import { WarehouseAnalytics } from "./internal/WarehouseAnalytics";
import { WarehouseCrm } from "./internal/WarehouseCrm";
import { UserMenu } from "../UserMenu";
import { ThemeToggle } from "../ThemeToggle";
import { ThemeProvider } from "../../contexts/ThemeContext";
import { useExtranetSection } from "../../hooks/useExtranetSection";
import { InternalSection, InternalPmsTab } from "../../lib/routes";

export function InternalExtranet() {
  const location = useLocation();
  // Section and PMS tab both live in the URL (/extranet/internal/:section/:sub)
  // so every page is bookmarkable and the back button walks the pages visited.
  const { section: activeSection, sub, params, sectionPath, goToSection } =
    useExtranetSection("internal");
  const activePMSSection: InternalPmsTab = sub === "scopes" ? "scopes" : "list";
  const [userEmail, setUserEmail] = useState("");

  // Search term handed to a listing when arriving from another one's relation chip,
  // so clicking "Mews" on a hotel row lands on Connectors already filtered to Mews.
  // It rides in the URL (?search=), which keeps the filtered view shareable.
  const openRelated = (section: InternalSection, term: string) =>
    goToSection(section, {
      sub: section === "pms" ? "list" : undefined,
      query: { search: term },
    });

  const searchFor = (section: InternalSection) =>
    activeSection === section ? params.get("search") ?? undefined : undefined;

  // CRM profile opened from the warehouse: the email travels in the URL too.
  const crmPrefillEmail = params.get("email");

  useEffect(() => {
    const getUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        setUserEmail(user.email || "");
      }
    };
    getUser();

    // Check if we should show settings with user tab. The state is carried
    // over so Settings still opens on the "user" tab; the section guard stops
    // the redirect from firing again once we're there.
    if (location.state?.settingsTab === "user" && activeSection !== "settings") {
      goToSection("settings", { replace: true, state: location.state });
    }
  }, [location.state, activeSection, goToSection]);

  const renderContent = () => {
    if (activeSection === "pms") {
      return (
        <div className="extranet-container">
          <div className="extranet-container-header">
            <div className="flex items-center">
              <Database className="h-6 w-6 text-indigo-600 mr-2" />
              <h2 className="text-2xl font-bold text-gray-900">Connectors</h2>
            </div>
          </div>

          <div className="extranet-container-body">
            {/* Tabs */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <Link
                  to={sectionPath("pms", { sub: "list" })}
                  aria-current={activePMSSection === "list" ? "page" : undefined}
                  className={`
                    flex items-center py-4 px-1 border-b-2 font-medium text-sm
                    ${
                      activePMSSection === "list"
                        ? "border-indigo-500 text-indigo-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }
                  `}
                >
                  <List className="h-5 w-5 mr-2" />
                  Connectors List
                </Link>
                <Link
                  to={sectionPath("pms", { sub: "scopes" })}
                  aria-current={activePMSSection === "scopes" ? "page" : undefined}
                  className={`
                    flex items-center py-4 px-1 border-b-2 font-medium text-sm
                    ${
                      activePMSSection === "scopes"
                        ? "border-indigo-500 text-indigo-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }
                  `}
                >
                  <Layers className="h-5 w-5 mr-2" />
                  Connector Scopes
                </Link>
              </nav>
            </div>

            {/* Content */}
            <div className="mt-6">
              {activePMSSection === "list" ? (
                <PMSTable
                  initialSearch={searchFor("pms")}
                  onOpenHotel={(hotelName) => openRelated("hotels", hotelName)}
                />
              ) : (
                <PMSScopes />
              )}
            </div>
          </div>
        </div>
      );
    }

    switch (activeSection) {
      case "hotels":
        return (
          <HotelAccounts
            initialSearch={searchFor("hotels")}
            onOpenConnector={(connectorCode) => openRelated("pms", connectorCode)}
            onOpenSaas={(saasCompany) => openRelated("saas", saasCompany)}
          />
        );
      case "saas":
        return (
          <SaasAccounts
            initialSearch={searchFor("saas")}
            onOpenHotel={(hotelName) => openRelated("hotels", hotelName)}
          />
        );
      case "logs":
        return <InternalLogs />;
      case "queues":
        return <QueueMonitoring />;
      case "warehouse":
        return (
          <WarehouseMonitoring
            onOpenCrmProfile={(email) =>
              goToSection("warehouse_crm", { query: { email } })
            }
          />
        );
      case "warehouse_analytics":
        return <WarehouseAnalytics />;
      case "warehouse_crm":
        return (
          <WarehouseCrm initialEmail={crmPrefillEmail} />
        );
      case "settings":
        return <InternalSettings />;
      default:
        return <PMSTable />;
    }
  };

  const menuItems: { id: InternalSection; label: string; icon: typeof Database }[] = [
    { id: "pms", label: "Connectors", icon: Database },
    { id: "hotels", label: "Hotel Accounts", icon: Building2 },
    { id: "saas", label: "SaaS Accounts", icon: Layout },
    { id: "logs", label: "System Logs", icon: ScrollText },
    { id: "queues", label: "Queue Monitoring", icon: Activity },
    { id: "warehouse", label: "Reservation Warehouse", icon: Warehouse },
    { id: "warehouse_analytics", label: "Warehouse statistics", icon: BarChart3 },
    { id: "warehouse_crm", label: "CRM profiles", icon: Users },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <ThemeProvider>
      <div className="extranet-layout">
        <div className="extranet-sidebar" style={{ backgroundColor: '#101829' }}>
          {/* Logo */}
          <div className="p-4">
            <div className="flex flex-col">
              <div className="w-40">
                <img
                  src="/images/zeswitch2.png"
                  alt="ZeSwitch"
                  className="w-full h-auto block"
                />
                <img
                  src="/images/zeswitch2.png"
                  alt="ZeSwitch"
                  className="w-full h-auto hidden"
                />
              </div>
              <span className="mt-1 text-sm font-medium text-gray-300">
                for Admins
              </span>
            </div>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 p-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.id}
                  to={sectionPath(item.id)}
                  aria-current={activeSection === item.id ? "page" : undefined}
                  className={`
                    w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                    ${
                      activeSection === item.id
                        ? "bg-[#8B5CF6] text-white"
                        : "text-gray-300 hover:bg-[#1a2332] hover:text-white"
                    }
                  `}
                >
                  <Icon
                    className={`
                    h-5 w-5 mr-3
                    ${
                      activeSection === item.id
                        ? "text-white"
                        : "text-gray-400"
                    }
                  `}
                  />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* User Menu and Theme Toggle */}
          <div className="p-4 flex items-center justify-between">
            <UserMenu email={userEmail} className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors" onOpenSettings={() => goToSection("settings")} />
            <ThemeToggle />
          </div>
        </div>

        {/* Main Content */}
        <div className="extranet-content">
          <div className="extranet-content-inner">{renderContent()}</div>
        </div>
      </div>
    </ThemeProvider>
  );
}
